import { datadogRum } from '@datadog/browser-rum';
import {
  looksLikeRelativeTime,
  getTimeStamp,
  RelativeTime,
  TimeStamp,
} from '@datadog/browser-core';

/**
 * Records a custom timing for the active view. This should be used for
 * performance-related timings. Use `recordMark` instead when recording
 * information about user interactions and other miscellaneous analytics.
 * See https://docs.datadoghq.com/real_user_monitoring/browser/monitoring_page_performance/#add-your-own-performance-timing
 * @param metricName Name of the custom timing
 * @param timing Optional timing information. When not set, the current time is
 *  used. This is only needed in rare cases where you are reporting on timing
 *  asynchronously. The timing should be a number that is one of the following:
 *   - A timestamp in milliseconds.
 *   - A duration in milliseconds that is relative to the initial page load.
 */
const recordCustomTiming = (metricName: string, timing?: number) => {
  if (!timing) {
    // No timing specified. DataDog will handle timing.
    datadogRum.addTiming(metricName);
  } else {
    // Modify the type of the timing to allow use of some DataDog core APIs
    // without type errors. They do this sort of casting of a `number` to these
    // types throughout their codebase to annotate times, so following that
    // pattern.
    const timingInternal = timing as RelativeTime | TimeStamp;

    // Always use a timestamp because DataDog's underlying implementation is only
    // "smart" about adjusting for route changes when passed a timestamp. Use the
    // timing utils from DataDog for as much consistency as possible.
    //
    // Relevant implementation details for additional context:
    //   - When `addTiming` receives a duration-based timing (time less than a
    //     year), it uses it as is. This requires durations to be relative to
    //     routing changes (not the initial page load) to work with SPAs that
    //     have their own routing.
    //   - When `addTiming` receives a timestamp-based timing, it converts it
    //     into a duration relative to the most recently tracked `view` (the
    //     initial page load or the most recent route change, depending on if
    //     the user has navigated or not).
    //   - We have internal use cases where we have duration values that are
    //     relative to the page load. We want those numbers tracked relative to
    //     routing changes. DataDog does not expose timing for routing changes,
    //     so we convert our durations into timestamps. When DataDog receives a
    //     timestamp, it follows the previously described behavior and translates
    //     the timestamp into a duration relative to the most recent view.
    //
    // https://github.com/DataDog/browser-sdk/blob/faf8441e2f7fa5b0791bf9eea474160bc58111e5/packages/rum-core/src/domain/rumEventsCollection/view/trackViews.ts#L225
    const timestamp = looksLikeRelativeTime(timingInternal)
      ? getTimeStamp(timingInternal)
      : timing;

    datadogRum.addTiming(metricName, timestamp);
  }
};

export default recordCustomTiming;
